/**
 * BookingsPage - List all studio bookings with filters
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getBookings,
  type BookingListItem,
  formatBookingStatus,
  getStatusColor,
} from '../../api/studios';
import { formatPrice } from '../../api/events';
import { parseLocalDate } from '../../utils/date';
import { DashboardMobileNav } from '../../components/dashboard';

type FilterStatus = 'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled';

export default function BookingsPage() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getBookings();
      setBookings(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    // "All" shows everything except cancelled - cancelled only shows when explicitly selected
    if (filterStatus === 'all') return booking.status !== 'cancelled';
    return booking.status === filterStatus;
  });

  const statusCounts = {
    all: bookings.filter((b) => b.status !== 'cancelled').length,
    pending: bookings.filter((b) => b.status === 'pending').length,
    confirmed: bookings.filter((b) => b.status === 'confirmed').length,
    completed: bookings.filter((b) => b.status === 'completed').length,
    cancelled: bookings.filter((b) => b.status === 'cancelled').length,
  };

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
                Dashboard
              </Link>
              <Link to="/dashboard/bookings" className="text-primary-600 font-medium text-sm">
                My Bookings
              </Link>
              <Link to="/dashboard/inquiries" className="text-warm-600 hover:text-warm-900 text-sm">
                My Inquiries
              </Link>
              <Link to="/dashboard/profile" className="text-warm-600 hover:text-warm-900 text-sm">
                Studio Profile
              </Link>
              <Link to="/dashboard/contacts" className="text-warm-600 hover:text-warm-900 text-sm">
                Contacts
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
              <button
                onClick={() => logout()}
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <DashboardMobileNav />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Breadcrumb — desktop only */}
        <nav className="mb-6 hidden sm:block">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/dashboard" className="text-primary-600 hover:text-primary-700">
                Dashboard
              </Link>
            </li>
            <li className="text-warm-400">/</li>
            <li className="text-warm-600">My Bookings</li>
          </ol>
        </nav>

        {/* Page header — compact on mobile */}
        <div className="flex items-center justify-between gap-3 mb-5 sm:mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">My Bookings</h1>
            <p className="text-warm-600 text-sm sm:text-base hidden sm:block">
              View and manage all your masterclass bookings.
            </p>
          </div>
          <Link to="/events" className="btn-primary whitespace-nowrap text-sm flex-shrink-0">
            <svg className="w-4 h-4 sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <span className="hidden sm:inline">Book New Class</span>
            <span className="sm:hidden">New Class</span>
          </Link>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-5 p-3 sm:p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-danger-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700 text-sm">{error}</span>
            <button
              onClick={loadBookings}
              className="ml-auto text-sm font-medium text-danger-600 hover:text-danger-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Filter Tabs — horizontally scrollable on mobile */}
        <div
          className="flex gap-2 mb-5 sm:mb-6 overflow-x-auto pb-1 sm:pb-0 sm:flex-wrap"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {(
            [
              { value: 'all', label: 'All' },
              { value: 'pending', label: 'Pending' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ] as { value: FilterStatus; label: string }[]
          ).map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterStatus(tab.value)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full sm:rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                filterStatus === tab.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-warm-600 hover:bg-warm-100 border border-warm-200'
              }`}
            >
              {tab.label}
              <span
                className={`ml-1.5 tabular-nums text-xs ${
                  filterStatus === tab.value
                    ? 'text-white/70'
                    : 'text-warm-400'
                }`}
              >
                {statusCounts[tab.value]}
              </span>
            </button>
          ))}
        </div>

        {/* Bookings List */}
        {isLoading ? (
          <div className="space-y-3 sm:space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm p-3 sm:p-6 animate-pulse">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="h-12 w-12 sm:h-16 sm:w-16 bg-warm-100 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-warm-100 rounded w-1/3" />
                    <div className="h-3 bg-warm-100 rounded w-1/2" />
                  </div>
                  <div className="h-6 bg-warm-100 rounded-full w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm text-center py-12 px-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-warm-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-warm-900 mb-2">
              {filterStatus === 'all' ? 'No bookings yet' : `No ${filterStatus} bookings`}
            </h3>
            <p className="text-warm-600 mb-6">
              {filterStatus === 'all'
                ? "You haven't booked any masterclasses yet. Start by finding a class near you!"
                : `You don't have any bookings with "${filterStatus}" status.`}
            </p>
            {filterStatus === 'all' && (
              <Link to="/events" className="btn-primary">
                Find Classes
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {filteredBookings.map((booking) => (
              <BookingCard key={booking.id} booking={booking} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * BookingCard - Individual booking card in the list
 */
interface BookingCardProps {
  booking: BookingListItem;
}

function BookingCard({ booking }: BookingCardProps) {
  const statusColors = getStatusColor(booking.status);
  // next_class_date is a date-only string — parseLocalDate avoids UTC midnight shift
  const classDate = booking.next_class_date
    ? parseLocalDate(booking.next_class_date)
    : null;

  return (
    <Link
      to={`/dashboard/bookings/${booking.id}`}
      className="block bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow group active:scale-[0.99] active:shadow-sm"
    >
      {/* Mobile layout */}
      <div className="sm:hidden p-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            {booking.show.logo_url ? (
              <img
                src={booking.show.logo_url}
                alt={booking.show.name}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <span className="text-primary-500 text-lg font-display font-bold">
                {booking.show.name.charAt(0)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-warm-900 text-[15px] truncate">
                {booking.show.name}
              </h3>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusColors}`}
              >
                {formatBookingStatus(booking.status)}
              </span>
            </div>
            <p className="text-sm text-warm-500 mt-0.5">
              {classDate ? classDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              }) : 'Date TBD'}
              {booking.venue.city ? ` · ${booking.venue.city}` : ''}
            </p>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-sm font-semibold text-warm-800">
                {formatPrice(booking.total_amount_cents)}
              </span>
              <span className="text-xs text-warm-400">
                {booking.class_count} class{booking.class_count !== 1 ? 'es' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden sm:block p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            {booking.show.logo_url ? (
              <img
                src={booking.show.logo_url}
                alt={booking.show.name}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <span className="text-primary-500 text-2xl font-display font-bold">
                {booking.show.name.charAt(0)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-warm-900 group-hover:text-primary-600 transition-colors truncate">
              {booking.show.name}
            </h3>
            <p className="text-sm text-warm-600 truncate">
              {booking.class_count} class{booking.class_count !== 1 ? 'es' : ''} •{' '}
              {classDate ? classDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }) : 'Date TBD'}
            </p>
            <p className="text-sm text-warm-500">
              {booking.venue.name}{booking.venue.city ? `, ${booking.venue.city}` : ''}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-semibold text-warm-900">
                {formatPrice(booking.total_amount_cents)}
              </div>
            </div>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors}`}
            >
              {formatBookingStatus(booking.status)}
            </span>

            <svg
              className="w-5 h-5 text-warm-400 group-hover:text-primary-600 transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
}
